(() => {
  'use strict';

  const selectors = {
    drawer: '[data-drawer]',
    drawerOpen: '[data-drawer-open]',
    drawerClose: '[data-drawer-close]',
    header: '[data-aura-header]',
    reveal: '[data-reveal]'
  };

  const storageKeys = {
    wishlist: 'attireAuraWishlist',
    recentlyViewed: 'attireAuraRecentlyViewed',
    cookieConsent: 'attireAuraCookieConsent'
  };

  const themeConfig = window.AttireAuraTheme || {};
  const themeRoutes = themeConfig.routes || {};
  const routes = {
    cart: themeRoutes.cart || '/cart',
    root: themeRoutes.root || '/'
  };
  const strings = themeConfig.strings || {};

  let activeDrawer = null;
  let lastFocusedElement = null;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }

    callback();
  }

  function escapeHTML(value) {
    return String(value || '').replace(/[&<>"']/g, (character) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return entities[character];
    });
  }

  function formatMoney(cents) {
    const currency = window.Shopify && Shopify.currency && Shopify.currency.active ? Shopify.currency.active : 'INR';
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency,
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2
      }).format(amount);
    } catch (error) {
      return `${currency} ${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`;
    }
  }

  function rootRoute(path) {
    const root = routes.root.endsWith('/') ? routes.root : `${routes.root}/`;
    return `${root}${path.replace(/^\/+/, '')}`;
  }

  function themeString(key, fallback) {
    return strings[key] || fallback;
  }

  function cartRoute(suffix = '') {
    return `${routes.cart.replace(/\/+$/, '')}${suffix}`;
  }

  function notify(message) {
    const liveRegion = document.getElementById('aura-live-region');
    if (liveRegion) liveRegion.textContent = message;
  }

  function readStorage(key, fallback = []) {
    try {
      return JSON.parse(window.localStorage.getItem(key)) || fallback;
    } catch (error) {
      return fallback;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Local storage may be unavailable in strict privacy contexts.
    }
  }

  function getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'
      )
    ).filter((element) => element.offsetParent !== null || element === document.activeElement);
  }

  function trapFocus(event) {
    if (!activeDrawer || event.key !== 'Tab') return;

    const panel = activeDrawer.querySelector('.drawer__panel');
    const focusableElements = panel ? getFocusableElements(panel) : [];

    if (!focusableElements.length) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  function openDrawer(name) {
    const drawer = document.querySelector(`${selectors.drawer}[data-drawer="${name}"]`);
    if (!drawer || activeDrawer === drawer) return;

    if (activeDrawer) closeDrawer(activeDrawer, false);

    lastFocusedElement = document.activeElement;
    drawer.hidden = false;
    activeDrawer = drawer;
    document.documentElement.classList.add('drawer-open');
    updateDrawerTriggers(name, true);

    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      const panel = drawer.querySelector('.drawer__panel');
      const autofocus = drawer.querySelector('[autofocus], input[type="search"]');
      const focusTarget = autofocus || panel;
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    });
  }

  function closeDrawer(drawer = activeDrawer, restoreFocus = true) {
    if (!drawer) return;

    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('drawer-open');
    updateDrawerTriggers(drawer.dataset.drawer, false);

    window.setTimeout(() => {
      drawer.hidden = true;

      if (activeDrawer === drawer) {
        activeDrawer = null;
      }

      if (restoreFocus && lastFocusedElement) {
        lastFocusedElement.focus({ preventScroll: true });
      }
    }, prefersReducedMotion ? 0 : 280);
  }

  function bindDrawers(root = document) {
    root.querySelectorAll(`${selectors.drawerOpen}:not([data-aura-bound])`).forEach((trigger) => {
      trigger.dataset.auraBound = 'true';
      const drawer = document.querySelector(`${selectors.drawer}[data-drawer="${trigger.dataset.drawerOpen}"]`);
      trigger.setAttribute('aria-expanded', 'false');
      if (drawer && drawer.id) {
        trigger.setAttribute('aria-controls', drawer.id);
      }
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        openDrawer(trigger.dataset.drawerOpen);
      });
    });

    root.querySelectorAll(`${selectors.drawerClose}:not([data-aura-bound])`).forEach((trigger) => {
      trigger.dataset.auraBound = 'true';
      trigger.addEventListener('click', () => closeDrawer(trigger.closest(selectors.drawer)));
    });
  }

  function updateDrawerTriggers(name, isOpen) {
    if (!name) return;
    document.querySelectorAll(`${selectors.drawerOpen}[data-drawer-open="${name}"]`).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  function bindKeyboard() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && activeDrawer) {
        closeDrawer(activeDrawer);
      }

      trapFocus(event);
    });
  }

  function bindHeader(root = document) {
    const header = root.querySelector(selectors.header) || document.querySelector(selectors.header);
    if (!header || header.dataset.auraHeaderBound === 'true') return;

    header.dataset.auraHeaderBound = 'true';

    let lastScrollY = window.scrollY;
    let ticking = false;

    function updateHeader() {
      const currentScrollY = window.scrollY;
      const isScrolled = currentScrollY > 12;
      const scrollingDown = currentScrollY > lastScrollY;
      const canHide = header.classList.contains('site-header--scroll-up') && currentScrollY > 120;

      header.classList.toggle('is-scrolled', isScrolled);
      header.classList.toggle('is-hidden', canHide && scrollingDown && !document.documentElement.classList.contains('drawer-open'));

      lastScrollY = Math.max(currentScrollY, 0);
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(updateHeader);
          ticking = true;
        }
      },
      { passive: true }
    );

    updateHeader();
  }

  function bindRevealAnimations(root = document) {
    if (document.body.dataset.revealAnimations !== 'true' || prefersReducedMotion || !('IntersectionObserver' in window)) {
      root.querySelectorAll(selectors.reveal).forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-visible');
          currentObserver.unobserve(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.12
      }
    );

    root.querySelectorAll(`${selectors.reveal}:not(.is-visible)`).forEach((element) => observer.observe(element));
  }

  function debounce(callback, delay = 180) {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), delay);
    };
  }

  function renderPredictiveResults(container, results) {
    const products = results.products || [];
    const collections = results.collections || [];
    const pages = results.pages || [];

    if (!products.length && !collections.length && !pages.length) {
      container.innerHTML = `<p class="predictive-search-results__empty">${escapeHTML(themeString('noResults', 'No results found'))}</p>`;
      return;
    }

    const productMarkup = products
      .map((product) => {
        const image = product.image ? `<img src="${product.image}" alt="" loading="lazy">` : '';
        const price = product.price ? `<span>${product.price}</span>` : '';
        return `
          <a class="predictive-search-card" href="${product.url}">
            ${image}
            <span>${escapeHTML(product.title)}</span>
            ${price}
          </a>
        `;
      })
      .join('');

    const collectionMarkup = collections
      .map(
        (collection) => `
          <a class="predictive-search-link" href="${collection.url}">
            <span>${escapeHTML(collection.title)}</span>
          </a>
        `
      )
      .join('');

    const pageMarkup = pages
      .map(
        (page) => `
          <a class="predictive-search-link" href="${page.url}">
            <span>${escapeHTML(page.title)}</span>
          </a>
        `
      )
      .join('');

    container.innerHTML = `${productMarkup}${collectionMarkup}${pageMarkup}`;
  }

  function bindPredictiveSearch(root = document) {
    root.querySelectorAll('[data-predictive-search-input]:not([data-aura-bound])').forEach((input) => {
      input.dataset.auraBound = 'true';

      const scope = input.closest('.drawer, .search-template') || document;
      const resultsWrap = scope.querySelector('[data-predictive-search-results]');
      const resultsList = scope.querySelector('[data-predictive-search-list]');

      if (!resultsWrap || !resultsList) return;

      const search = debounce(async () => {
        const query = input.value.trim();
        if (query.length < 2) {
          resultsWrap.hidden = true;
          resultsList.innerHTML = '';
          return;
        }

        try {
          const response = await fetch(
            `${rootRoute('search/suggest.json')}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=6`
          );
          const json = await response.json();
          renderPredictiveResults(resultsList, json.resources.results || {});
          resultsWrap.hidden = false;
        } catch (error) {
          resultsWrap.hidden = true;
        }
      }, 220);

      input.addEventListener('input', search);
    });
  }

  function updateWishlistUI(root = document) {
    const wishlist = readStorage(storageKeys.wishlist);
    const handles = wishlist.map((item) => item.handle);

    document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
      count.textContent = wishlist.length;
      count.hidden = wishlist.length === 0;
    });

    root.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const active = handles.includes(button.dataset.productHandle);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    const itemsContainer = document.querySelector('[data-wishlist-items]');
    const emptyContainer = document.querySelector('[data-wishlist-empty]');
    if (!itemsContainer || !emptyContainer) return;

    itemsContainer.innerHTML = wishlist
      .map(
        (item) => `
          <article class="wishlist-item">
            ${item.image ? `<img src="${item.image}" alt="" loading="lazy">` : ''}
            <div>
              <a href="${item.url}">${escapeHTML(item.title)}</a>
              <span>${escapeHTML(item.price)}</span>
            </div>
            <button class="icon-button" type="button" data-wishlist-remove="${escapeHTML(item.handle)}" aria-label="${escapeHTML(themeString('remove', 'Remove'))} ${escapeHTML(item.title)}">
              <svg class="icon icon-close" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M5 5l14 14"></path><path d="M19 5 5 19"></path></svg>
            </button>
          </article>
        `
      )
      .join('');

    emptyContainer.hidden = wishlist.length > 0;
    renderWishlistPage();
  }

  function bindWishlist(root = document) {
    root.querySelectorAll('[data-wishlist-toggle]:not([data-wishlist-bound])').forEach((button) => {
      button.dataset.wishlistBound = 'true';
      button.addEventListener('click', () => {
        const item = {
          handle: button.dataset.productHandle,
          title: button.dataset.productTitle,
          url: button.dataset.productUrl,
          price: button.dataset.productPrice,
          image: button.dataset.productImage || ''
        };

        const wishlist = readStorage(storageKeys.wishlist);
        const exists = wishlist.some((entry) => entry.handle === item.handle);
        const nextWishlist = exists ? wishlist.filter((entry) => entry.handle !== item.handle) : [item, ...wishlist].slice(0, 24);
        writeStorage(storageKeys.wishlist, nextWishlist);
        updateWishlistUI(document);
        notify(exists ? themeString('wishlistRemoved', 'Removed from wishlist') : themeString('wishlistAdded', 'Added to wishlist'));
      });
    });

    document.querySelectorAll('[data-wishlist-items]:not([data-wishlist-remove-bound])').forEach((container) => {
      container.dataset.wishlistRemoveBound = 'true';
      container.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-wishlist-remove]');
        if (!removeButton) return;

        const wishlist = readStorage(storageKeys.wishlist).filter((item) => item.handle !== removeButton.dataset.wishlistRemove);
        writeStorage(storageKeys.wishlist, wishlist);
        updateWishlistUI(document);
      });
    });

    updateWishlistUI(root);
  }

  async function updateCartCount() {
    try {
      const cart = await fetchCart();
      document.querySelectorAll('.site-header__cart-count').forEach((count) => {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
      });
    } catch (error) {
      // The regular cart page remains the reliable fallback.
    }
  }

  async function fetchCart() {
    const response = await fetch(cartRoute('.js'));
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }

  async function addToCart(body, headers = { Accept: 'application/json' }) {
    const response = await fetch(rootRoute('cart/add.js'), {
      method: 'POST',
      headers,
      body
    });

    if (!response.ok) throw new Error('Add to cart failed');
    return response.json();
  }

  function updateShippingProgress(cart = null) {
    document.querySelectorAll('[data-shipping-progress]').forEach((progress) => {
      const threshold = Number(progress.dataset.threshold || 0);
      const total = Number((cart && cart.total_price) || progress.dataset.cartTotal || 0);
      const text = progress.querySelector('[data-shipping-progress-text]');
      const bar = progress.querySelector('[data-shipping-progress-bar]');
      const percent = threshold > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 100;
      const remaining = Math.max(threshold - total, 0);

      if (text) {
        text.textContent =
          remaining === 0
            ? themeString('shippingComplete', 'Complimentary shipping unlocked')
            : themeString('shippingRemaining', '[amount] away from complimentary shipping').replace('[amount]', formatMoney(remaining));
      }

      if (bar) {
        bar.style.width = `${percent}%`;
      }
    });
  }

  function updateCartIndicators(cart) {
    document.querySelectorAll('[data-cart-total]').forEach((total) => {
      total.textContent = formatMoney(cart.total_price);
    });

    document.querySelectorAll('.site-header__cart-count').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    });

    updateShippingProgress(cart);
  }

  function parseSection(markup) {
    return new DOMParser().parseFromString(markup, 'text/html');
  }

  function replaceElementFromSection(markup, currentElement, incomingSelector) {
    if (!markup || !currentElement) return false;

    const incomingElement = parseSection(markup).querySelector(incomingSelector);
    if (!incomingElement) return false;

    if (activeDrawer && (activeDrawer === currentElement || currentElement.contains(activeDrawer))) {
      activeDrawer = null;
    }

    currentElement.replaceWith(incomingElement);
    return true;
  }

  async function renderCartSections(options = {}) {
    const sectionIds = ['cart-drawer'];
    const currentCartPage = document.querySelector('[data-cart-page-form]');
    const currentCartSection = currentCartPage ? currentCartPage.closest('[data-section-id]') : null;

    if (currentCartPage) {
      sectionIds.push('main');
    }

    const cartUrl = cartRoute();
    const separator = cartUrl.includes('?') ? '&' : '?';
    const response = await fetch(`${cartUrl}${separator}sections=${sectionIds.join(',')}`, {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error('Cart sections failed');

    const sections = await response.json();
    const shouldReopenDrawer = options.keepDrawerOpen || Boolean(document.querySelector('[data-drawer="cart"].is-open'));
    const currentDrawer = document.querySelector('[data-drawer="cart"]');

    replaceElementFromSection(sections['cart-drawer'], currentDrawer, '[data-drawer="cart"]');

    if (sections.main && currentCartSection) {
      const incomingCartPage = parseSection(sections.main).querySelector('[data-cart-page-form]');
      const incomingCartSection = incomingCartPage ? incomingCartPage.closest('[data-section-id]') : null;
      if (incomingCartSection) {
        currentCartSection.replaceWith(incomingCartSection);
      }
    }

    init(document);

    if (shouldReopenDrawer) {
      openDrawer('cart');
    }
  }

  async function refreshCartUI(cart = null, options = {}) {
    const resolvedCart = cart || (await fetchCart());
    updateCartIndicators(resolvedCart);

    try {
      await renderCartSections(options);
    } catch (error) {
      // Totals and counts are still refreshed if section rendering is temporarily unavailable.
    }

    updateCartIndicators(resolvedCart);
    return resolvedCart;
  }

  function renderQuickView(product) {
    const variant = product.variants.find((item) => item.available) || product.variants[0];
    const image = product.featured_image ? `<img src="${product.featured_image}" alt="" loading="lazy">` : '';
    const options = product.variants
      .map((item) => `<option value="${item.id}" ${item.id === variant.id ? 'selected' : ''} ${item.available ? '' : 'disabled'}>${escapeHTML(item.title)} - ${formatMoney(item.price)}</option>`)
      .join('');

    return `
      <div class="quick-view__grid">
        <a class="quick-view__media media" href="${product.url}">
          ${image}
        </a>
        <div class="quick-view__details">
          <p class="eyebrow">${escapeHTML(themeString('quickView', 'Quick view'))}</p>
          <h2 class="h3"><a href="${product.url}">${escapeHTML(product.title)}</a></h2>
          <p class="quick-view__price">${formatMoney(variant.price)}</p>
          <form class="quick-view__form" data-quick-view-form>
            <input type="hidden" name="id" value="${variant.id}" data-quick-view-variant-id>
            ${
              product.variants.length > 1
                ? `<label class="field__label" for="QuickViewVariant">${escapeHTML(themeString('options', 'Options'))}</label><select class="select" id="QuickViewVariant" data-quick-view-select>${options}</select>`
                : ''
            }
            <div class="quick-view__actions">
              <input class="quantity__input" type="number" name="quantity" value="1" min="1" aria-label="${escapeHTML(themeString('quantity', 'Quantity'))}">
              <button class="button button--filled" type="submit" ${variant.available ? '' : 'disabled'}>${variant.available ? escapeHTML(themeString('addToCart', 'Add to cart')) : escapeHTML(themeString('soldOut', 'Sold out'))}</button>
            </div>
          </form>
          <a class="text-link" href="${product.url}">${escapeHTML(themeString('viewFullDetails', 'View full details'))}</a>
        </div>
      </div>
    `;
  }

  function bindQuickView(root = document) {
    root.querySelectorAll('[data-quick-view]:not([data-quick-view-bound])').forEach((button) => {
      button.dataset.quickViewBound = 'true';
      button.addEventListener('click', async () => {
        const drawer = document.querySelector('[data-drawer="quick-view"]');
        const container = document.querySelector('[data-quick-view-content]');
        if (!drawer || !container) return;

        container.innerHTML = `<div class="quick-view__loading">${escapeHTML(themeString('loading', 'Loading'))}</div>`;
        openDrawer('quick-view');

        try {
          const response = await fetch(rootRoute(`products/${encodeURIComponent(button.dataset.productHandle)}.js`));
          const product = await response.json();
          container.innerHTML = renderQuickView(product);
          bindQuickViewForm(container, product);
        } catch (error) {
          container.innerHTML = '<p class="quick-view__loading">This piece could not be loaded.</p>';
        }
      });
    });
  }

  function bindQuickViewForm(root, product) {
    const select = root.querySelector('[data-quick-view-select]');
    const variantInput = root.querySelector('[data-quick-view-variant-id]');
    const price = root.querySelector('.quick-view__price');

    if (select && variantInput) {
      select.addEventListener('change', () => {
        const variant = product.variants.find((item) => String(item.id) === select.value);
        if (!variant) return;
        variantInput.value = variant.id;
        if (price) price.textContent = formatMoney(variant.price);
      });
    }

    root.querySelectorAll('[data-quick-view-form]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('[type="submit"]');
        button.disabled = true;

        try {
          await addToCart(new FormData(form));
          notify(themeString('addedToCart', 'Added to cart'));
          await refreshCartUI();
          openDrawer('cart');
        } catch (error) {
          notify(themeString('addToCartError', 'Unable to add this piece'));
        } finally {
          button.disabled = false;
        }
      });
    });
  }

  function bindQuantity(root = document) {
    root.querySelectorAll('[data-quantity-input]:not([data-quantity-bound])').forEach((quantity) => {
      quantity.dataset.quantityBound = 'true';
      const input = quantity.querySelector('input');
      quantity.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button || !input) return;

        const current = Number(input.value || 1);
        input.value = button.name === 'plus' ? current + 1 : Math.max(1, current - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function bindGallery(root = document) {
    root.querySelectorAll('[data-product-gallery]:not([data-gallery-bound])').forEach((gallery) => {
      gallery.dataset.galleryBound = 'true';
      gallery.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-product-thumbnail]');
        if (!thumb) return;
        activateMedia(gallery, thumb.dataset.mediaId);
      });
    });
  }

  function activateMedia(gallery, mediaId) {
    if (!gallery || !mediaId) return;

    gallery.querySelectorAll('[data-product-media]').forEach((media) => {
      const active = media.dataset.mediaId === String(mediaId);
      media.classList.toggle('is-active', active);
      media.hidden = !active;
    });

    gallery.querySelectorAll('[data-product-thumbnail]').forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb.dataset.mediaId === String(mediaId));
    });
  }

  function bindProductVariants(root = document) {
    root.querySelectorAll('[data-product-section]:not([data-variant-bound])').forEach((section) => {
      section.dataset.variantBound = 'true';
      const json = section.querySelector('[data-product-json]');
      const form = section.querySelector('[data-product-form]');
      if (!json || !form) return;

      const product = JSON.parse(json.textContent);
      const labels = {
        add: section.dataset.labelAdd || 'Add to cart',
        sold: section.dataset.labelSold || 'Sold out',
        unavailable: section.dataset.labelUnavailable || themeString('unavailable', 'Unavailable')
      };

      function getSelectedOptions() {
        return product.options.map((optionName) => {
          const checked = Array.from(form.querySelectorAll('input[type="radio"]')).find(
            (input) => input.name === `options[${optionName}]` && input.checked
          );
          return checked ? checked.value : null;
        });
      }

      function findVariant() {
        const selectedOptions = getSelectedOptions();
        return product.variants.find((variant) => variant.options.every((option, index) => option === selectedOptions[index]));
      }

      function updateVariant() {
        const variant = findVariant();
        const addButton = section.querySelector('[data-add-to-cart]');
        const addText = section.querySelector('[data-add-to-cart-text]');
        const stickyButton = section.querySelector('[data-sticky-add-to-cart]');
        const stickyText = section.querySelector('[data-sticky-add-to-cart-text]');
        const variantInput = section.querySelector('[data-variant-id]');
        const stickyVariantInput = section.querySelector('[data-sticky-variant-id]');
        const price = section.querySelector('[data-product-price]');
        const stickyPrice = section.querySelector('[data-sticky-price]');
        const quantity = section.querySelector('[data-quantity-input] input');
        const stickyQuantity = section.querySelector('[data-sticky-quantity]');

        section.querySelectorAll('[data-selected-option]').forEach((label) => {
          const position = Number(label.dataset.selectedOption) - 1;
          const selected = getSelectedOptions()[position];
          if (selected) label.textContent = selected;
        });

        if (!variant) {
          [addButton, stickyButton].forEach((button) => {
            if (button) button.disabled = true;
          });
          [addText, stickyText].forEach((text) => {
            if (text) text.textContent = labels.unavailable;
          });
          return;
        }

        if (variantInput) variantInput.value = variant.id;
        if (stickyVariantInput) stickyVariantInput.value = variant.id;
        if (stickyQuantity && quantity) stickyQuantity.value = quantity.value;

        const priceMarkup = `${variant.compare_at_price > variant.price ? `<s>${formatMoney(variant.compare_at_price)}</s>` : ''}<span>${formatMoney(variant.price)}</span>`;
        if (price) price.innerHTML = priceMarkup;
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

        [addButton, stickyButton].forEach((button) => {
          if (button) button.disabled = !variant.available;
        });

        [addText, stickyText].forEach((text) => {
          if (text) text.textContent = variant.available ? labels.add : labels.sold;
        });

        if (variant.featured_media && variant.featured_media.id) {
          activateMedia(section.querySelector('[data-product-gallery]'), variant.featured_media.id);
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      form.addEventListener('change', updateVariant);
      const quantity = section.querySelector('[data-quantity-input] input');
      if (quantity) {
        quantity.addEventListener('change', () => {
          const stickyQuantity = section.querySelector('[data-sticky-quantity]');
          if (stickyQuantity) stickyQuantity.value = quantity.value;
        });
      }

      updateVariant();
    });
  }

  function bindSubmitOnChange(root = document) {
    root.querySelectorAll('[data-submit-on-change]:not([data-submit-bound])').forEach((input) => {
      input.dataset.submitBound = 'true';
      input.addEventListener('change', () => {
        const form = input.closest('form');
        if (form) form.submit();
      });
    });
  }

  function bindRecommendations(root = document) {
    root.querySelectorAll('[data-product-recommendations]:not([data-recommendations-bound])').forEach(async (section) => {
      section.dataset.recommendationsBound = 'true';
      const url = section.dataset.url;
      if (!url || section.innerText.trim()) return;

      try {
        const response = await fetch(url);
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const recommendations = html.querySelector('[data-product-recommendations]');
        if (recommendations && recommendations.innerHTML.trim()) {
          section.innerHTML = recommendations.innerHTML;
          init(section);
        }
      } catch (error) {
        // Recommendations are progressive enhancement.
      }
    });
  }

  function bindRecentlyViewed(root = document) {
    root.querySelectorAll('[data-recently-viewed-list]:not([data-recently-bound])').forEach((list) => {
      list.dataset.recentlyBound = 'true';
      const section = list.closest('[data-recently-viewed-section]');
      const current = {
        handle: list.dataset.currentHandle,
        title: list.dataset.currentTitle,
        url: list.dataset.currentUrl,
        price: list.dataset.currentPrice,
        image: list.dataset.currentImage || ''
      };

      const previous = readStorage(storageKeys.recentlyViewed).filter((item) => item.handle && item.handle !== current.handle);
      const visibleItems = previous.slice(0, 4);

      if (!visibleItems.length && section) {
        section.hidden = true;
      } else if (section) {
        section.hidden = false;
      }

      list.innerHTML = visibleItems
        .map(
          (item) => `
            <article class="product-card" data-reveal>
              <div class="product-card__frame">
                <a class="product-card__media media" href="${item.url}">
                  ${item.image ? `<img class="product-card__image product-card__image--primary" src="${item.image}" alt="" loading="lazy">` : ''}
                </a>
              </div>
              <div class="product-card__content">
                <h3 class="product-card__title"><a href="${item.url}">${escapeHTML(item.title)}</a></h3>
                <div class="product-card__price"><span>${escapeHTML(item.price)}</span></div>
              </div>
            </article>
          `
        )
        .join('');

      if (current.handle) {
        writeStorage(storageKeys.recentlyViewed, [current, ...previous].slice(0, 12));
      }
    });
  }

  function bindFrequentlyBought(root = document) {
    root.querySelectorAll('[data-fbt-add]:not([data-fbt-bound])').forEach((button) => {
      button.dataset.fbtBound = 'true';
      button.addEventListener('click', async () => {
        const ids = (button.dataset.variantIds || '').split(',').filter(Boolean);
        if (!ids.length) return;

        button.disabled = true;

        try {
          await addToCart(
            JSON.stringify({
              items: ids.map((id) => ({ id: Number(id), quantity: 1 }))
            }),
            {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            }
          );
          notify(themeString('selectedAddedToCart', 'Selected pieces added to cart'));
          await refreshCartUI();
          openDrawer('cart');
        } catch (error) {
          notify(themeString('selectedAddError', 'Unable to add selected pieces'));
        } finally {
          button.disabled = false;
        }
      });
    });
  }

  function bindCookieBanner(root = document) {
    const banner = root.querySelector('[data-cookie-banner]') || document.querySelector('[data-cookie-banner]');
    if (!banner || banner.dataset.cookieBound === 'true') return;
    banner.dataset.cookieBound = 'true';

    if (window.localStorage.getItem(storageKeys.cookieConsent)) return;

    banner.hidden = false;
    requestAnimationFrame(() => banner.classList.add('is-visible'));

    banner.querySelectorAll('[data-cookie-accept], [data-cookie-decline]').forEach((button) => {
      button.addEventListener('click', () => {
        window.localStorage.setItem(storageKeys.cookieConsent, button.hasAttribute('data-cookie-accept') ? 'accepted' : 'declined');
        banner.classList.remove('is-visible');
        window.setTimeout(() => {
          banner.hidden = true;
        }, prefersReducedMotion ? 0 : 240);
      });
    });
  }

  function renderWishlistPage() {
    const list = document.querySelector('[data-wishlist-page-list]');
    const empty = document.querySelector('[data-wishlist-page-empty]');
    if (!list || !empty) return;

    const wishlist = readStorage(storageKeys.wishlist);
    empty.hidden = wishlist.length > 0;
    list.hidden = wishlist.length === 0;

    list.innerHTML = wishlist
      .map(
        (item) => `
          <article class="product-card wishlist-page-card" data-reveal>
            <div class="product-card__frame">
              <a class="product-card__media media" href="${item.url}">
                ${item.image ? `<img class="product-card__image product-card__image--primary" src="${item.image}" alt="" loading="lazy">` : ''}
              </a>
              <button class="wishlist-button is-active" type="button" data-wishlist-remove="${escapeHTML(item.handle)}" aria-label="${escapeHTML(themeString('remove', 'Remove'))} ${escapeHTML(item.title)}">
                <svg class="icon icon-close" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M5 5l14 14"></path><path d="M19 5 5 19"></path></svg>
              </button>
            </div>
            <div class="product-card__content">
              <h2 class="product-card__title"><a href="${item.url}">${escapeHTML(item.title)}</a></h2>
              <div class="product-card__price"><span>${escapeHTML(item.price)}</span></div>
            </div>
          </article>
        `
      )
      .join('');
  }

  function bindWishlistPage(root = document) {
    const pageList = root.querySelector('[data-wishlist-page-list]') || document.querySelector('[data-wishlist-page-list]');
    if (!pageList || pageList.dataset.wishlistPageBound === 'true') return;

    pageList.dataset.wishlistPageBound = 'true';
    renderWishlistPage();

    pageList.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wishlist-remove]');
      if (!button) return;

      const wishlist = readStorage(storageKeys.wishlist).filter((item) => item.handle !== button.dataset.wishlistRemove);
      writeStorage(storageKeys.wishlist, wishlist);
      updateWishlistUI(document);
      renderWishlistPage();
    });
  }

  async function changeCartLine(key, quantity) {
    const response = await fetch(rootRoute('cart/change.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: key, quantity })
    });

    if (!response.ok) throw new Error('Cart change failed');
    return response.json();
  }

  function bindCart(root = document) {
    root.querySelectorAll('[data-product-form]:not([data-cart-add-bound]), [data-sticky-product-form]:not([data-cart-add-bound])').forEach((form) => {
      form.dataset.cartAddBound = 'true';
      form.addEventListener('submit', async (event) => {
        const activeSubmitter = event.submitter || (form.contains(document.activeElement) ? document.activeElement : null);
        const isAddToCart =
          activeSubmitter &&
          (activeSubmitter.matches('[data-add-to-cart], [data-sticky-add-to-cart]') || activeSubmitter.name === 'add');

        if (!isAddToCart) return;

        event.preventDefault();
        const button = activeSubmitter;
        if (button) button.disabled = true;

        try {
          await addToCart(new FormData(form));
          notify(themeString('addedToCart', 'Added to cart'));
          await refreshCartUI();
          openDrawer('cart');
        } catch (error) {
          notify(themeString('addToCartError', 'Unable to add this piece'));
        } finally {
          if (button) button.disabled = false;
        }
      });
    });

    document.querySelectorAll('[data-cart-items]:not([data-cart-items-bound])').forEach((container) => {
      container.dataset.cartItemsBound = 'true';
      container.addEventListener('click', async (event) => {
        const removeButton = event.target.closest('[data-cart-remove]');
        if (!removeButton) return;

        removeButton.disabled = true;
        try {
          const cart = await changeCartLine(removeButton.dataset.key, 0);
          await refreshCartUI(cart, { keepDrawerOpen: Boolean(container.closest('[data-drawer="cart"]')) });
          notify(themeString('cartRemoved', 'Removed from cart'));
        } catch (error) {
          notify(themeString('cartUpdateError', 'Unable to update cart'));
        }
      });

      container.addEventListener('change', async (event) => {
        const input = event.target.closest('.quantity__input');
        const line = event.target.closest('[data-cart-line]');
        if (!input || !line) return;

        input.disabled = true;
        try {
          const cart = await changeCartLine(line.dataset.key, Math.max(0, Number(input.value || 0)));
          await refreshCartUI(cart, { keepDrawerOpen: Boolean(container.closest('[data-drawer="cart"]')) });
        } catch (error) {
          notify(themeString('cartUpdateError', 'Unable to update cart'));
        }
      });
    });

    document.querySelectorAll('[data-cart-form]:not([data-cart-form-bound])').forEach((form) => {
      form.dataset.cartFormBound = 'true';
      form.addEventListener('submit', (event) => {
        const submitter = event.submitter;
        if (!submitter || submitter.name !== 'checkout') return;

        const requiredTerms = form.querySelector('[data-terms-checkbox]');
        if (requiredTerms && !requiredTerms.checked) {
          event.preventDefault();
          requiredTerms.focus();
          notify('Please agree to the store policies before checkout');
        }
      });
    });

    updateShippingProgress();
  }

  function init(root = document) {
    bindDrawers(root);
    bindHeader(root);
    bindRevealAnimations(root);
    bindPredictiveSearch(root);
    bindWishlist(root);
    bindQuickView(root);
    bindQuantity(root);
    bindGallery(root);
    bindProductVariants(root);
    bindSubmitOnChange(root);
    bindRecommendations(root);
    bindRecentlyViewed(root);
    bindFrequentlyBought(root);
    bindCookieBanner(root);
    bindWishlistPage(root);
    bindCart(root);
  }

  onReady(() => {
    if (document.body.dataset.pageTransitions === 'true') {
      requestAnimationFrame(() => document.body.classList.add('is-loaded'));
    }

    init();
    bindKeyboard();
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
